import { useEffect, useState } from "react";
import { Badge } from "@/ui/badge";
import * as api from "../api.js";
import { useAcoes, useEstado, usePode } from "../estado.jsx";
import { useNavegacao } from "../navegacao.jsx";
import { dataHora, ICONES, Icone, mmss } from "../visual.jsx";
import { Esqueleto, MenuDeAcoes, Secao } from "./comuns.jsx";
import { confirmar } from "@/componentes/pergunta.tsx";
import { PlayerDeVoz } from "./Anexo.jsx";
import TranscricaoTocavel from "./TranscricaoTocavel.jsx";
import VideoDaReuniao from "./VideoDaReuniao.jsx";

/**
 * Uma reunião, inteira — o segundo degrau da coluna.
 *
 * Era um acordeão dentro de uma lista dentro de uma aba: transcrição de
 * quarenta minutos, vídeo e marcadores empilhados debaixo do cartão, com a
 * lista das outras reuniões ainda por cima e por baixo. Aqui a coluna vira a
 * reunião — o cabeçalho troca o menu pelo "voltar para a ficha", e a leitura
 * ganha a largura toda.
 *
 * O conteúdo pesado (áudio, transcrição, vídeo) só é buscado quando alguém abre
 * de fato: a lista do caso não o traz, e este componente não existe até o
 * `?gravacao=` apontar para cá.
 */
export default function ReuniaoAberta({ id }) {
  const { caso } = useEstado();
  const { citarMomento, carregarCaso, avisar } = useAcoes();
  const { ajustar } = useNavegacao();
  /* Apagar a gravação é a mesma permissão de gravá-la e renomeá-la: quem
     conduz a reunião é quem sabe que aquela ficou pela metade. */
  const podeApagar = usePode()("alinhamento:transcrever");
  const [cheia, setCheia] = useState(null);
  const [erro, setErro] = useState(null);
  /* O que a lista do caso já sabe: assunto, data e duração aparecem ANTES de a
     resposta chegar, em vez de a coluna piscar em branco. */
  const previa = (caso?.gravacoes ?? []).find((g) => String(g.id) === String(id)) ?? null;

  useEffect(() => {
    let vivo = true;
    setCheia(null);
    setErro(null);
    api.gravacao(id).then(
      (r) => vivo && setCheia(r),
      (e) => vivo && setErro(e?.message ?? "A reunião não veio."),
    );
    return () => {
      vivo = false;
    };
  }, [id]);

  const g = cheia ?? previa;
  const audio = g?.tem_audio ? { url: api.audioDaGravacao(g.id), nome: g.audio_arquivo || `reuniao-${g.id}.webm` } : null;

  return (
    <div className="px-4 pb-8">
      <div className="mt-3 flex items-start gap-2.5">
        <span className="mt-[2px] flex size-[22px] flex-none items-center justify-center rounded-md bg-verde-fundo/12">
          <Icone de={ICONES.calls} tamanho={14} cor="var(--color-verde-fundo)" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="font-serif text-forte leading-[1.3] text-balance [overflow-wrap:anywhere]">
            {g?.assunto || g?.titulo || "Reunião"}
          </div>
          <div className="mt-[3px] flex flex-wrap items-center gap-x-1.5 font-mono text-selo text-muted-foreground">
            <span>{dataHora(g?.criado_em)}</span>
            <span aria-hidden="true">·</span>
            <span>{mmss(g?.duracao_seg ?? 0)}</span>
          </div>
        </div>
        {/*
         * Apagar a reunião existia só na vista `/reunioes` — e não é lá que
         * alguém descobre que a gravação ficou pela metade: é aqui, abrindo a
         * transcrição do caso e vendo quarenta segundos de silêncio ou a
         * reunião gravada em duplicidade. A pergunta diz o que vai embora
         * (áudio, vídeo, transcrição, combinados), porque não volta.
         */}
        {podeApagar ? (
          <MenuDeAcoes
            rotulo="O que fazer com esta reunião"
            titulo="Apagar esta gravação"
            legenda={g?.assunto || g?.titulo || "Reunião"}
            ancora="bottom end"
            largura="252px"
            classe="flex size-[26px] flex-none items-center justify-center rounded-md text-muted-foreground hover:text-foreground"
            itens={[
              {
                chave: "apagar",
                rotulo: "Apagar a reunião",
                detalhe: "Some o áudio, o vídeo, a transcrição e os combinados.",
                icone: ICONES.apagar,
                perigo: true,
                aoTocar: async () => {
                  if (
                    !(await confirmar({
                      titulo: `Apagar “${g?.assunto || g?.titulo || "esta reunião"}”?`,
                      texto:
                        "Some o áudio, o vídeo, a transcrição, os momentos marcados e os combinados. Não dá para desfazer — os fatos já citados na peça ficam.",
                      acao: "Apagar a reunião",
                      perigo: true,
                    }))
                  )
                    return;
                  try {
                    await api.apagarGravacao(id);
                    /* Volta para a lista ANTES de reler: a coluna está montada
                       sobre uma gravação que não existe mais, e reler primeiro
                       a deixaria pedindo o que o servidor já apagou. */
                    ajustar({ gravacao: null, painel: "reunioes" });
                    await carregarCaso(caso?.registro?.id);
                    avisar("Reunião apagada.");
                  } catch (e) {
                    avisar(e.message);
                  }
                },
              },
            ]}
          >
            <Icone de={ICONES.mais} tamanho={16} largura={1.9} />
          </MenuDeAcoes>
        ) : null}
      </div>

      {audio ? (
        <div className="mt-3">
          <PlayerDeVoz anexo={audio} duracao={g.duracao_seg ?? 0} />
        </div>
      ) : null}

      {erro ? <div className="mt-3 text-apoio leading-[1.5] text-primary">{erro}</div> : null}

      {!cheia && !erro ? (
        <div className="mt-3.5">
          <Esqueleto linhas={2} altura={80} rotulo="Abrindo a reunião…" />
        </div>
      ) : null}

      {cheia?.itens?.length ? (
        <>
          <Secao espaco="18px 0 6px">O QUE FICOU COMBINADO</Secao>
          <div className="rounded-xl border border-foreground/10 bg-card px-3 py-1">
            {cheia.itens.map((i) => (
              <div
                key={i.id}
                className="flex items-baseline gap-2 border-t border-t-foreground/7 py-2 text-apoio leading-[1.5] first:border-t-0"
              >
                <span className="mt-[5px] size-[5px] flex-none rounded-full bg-verde" />
                <span className="min-w-0 flex-1 text-tinta-media">{i.texto}</span>
                {i.responsavel ? <span className="flex-none text-selo text-muted-foreground">{i.responsavel}</span> : null}
              </div>
            ))}
          </div>
        </>
      ) : null}

      {/* O vídeo vem ANTES da transcrição: quem abriu a reunião para ver a tela
          não devia rolar cinquenta falas para achar o quadro. */}
      {cheia?.tem_video ? (
        <div className="mt-4">
          <VideoDaReuniao gravacao={cheia} />
        </div>
      ) : null}

      {cheia?.marcadores?.length ? (
        <>
          <Secao espaco="18px 0 6px">MOMENTOS MARCADOS · CLIQUE PARA CITAR</Secao>
          <div className="flex flex-wrap gap-[5px]">
            {cheia.marcadores.map((m) => (
              /* Pastilha que é botão de verdade (`render`): o selo tem a cara de
                 etiqueta, mas clicar nele cita o momento na peça. */
              <Badge
                key={m.id}
                render={<button type="button" />}
                onClick={() => citarMomento({ segundo: m.segundo, texto: m.rotulo })}
                data-dica="Citar como prova do caso"
                className="h-auto max-w-full rounded-md border-verde-fundo/30 bg-verde-fundo/9 px-[7px] py-[3px] text-left text-apoio leading-[1.4] font-bold whitespace-normal text-verde-fundo"
              >
                <span className="font-mono">{mmss(m.segundo)}</span>
                {m.rotulo ? ` · ${m.rotulo}` : ""}
              </Badge>
            ))}
          </div>
        </>
      ) : null}

      {cheia?.transcricao || previa?.transcricao ? (
        <>
          <Secao espaco="18px 0 6px">TRANSCRIÇÃO</Secao>
          <TranscricaoTocavel
            transcricao={cheia?.transcricao || previa?.transcricao}
            altura="420px"
            duracao={g?.duracao_seg ?? 0}
            audio={audio}
          />
        </>
      ) : null}
    </div>
  );
}
